import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

type Tier = "Micro" | "Small" | "Medium" | "Large" | "XLarge" | "Enterprise";

interface ActiveOrgTier {
  companySizeTier: Tier | null;
  employeeCount: number | null;
  autoAdjustBillingTier: boolean;
  billingTierOverride: Tier | null;
  lastTierReviewAt: string | null;
  effectiveBillingTier: Tier | null;
  isBillingExempt: boolean;
}

const TIER_LABEL: Record<Tier, string> = {
  Micro: "Micro (≤10 employees)",
  Small: "Small (11–25)",
  Medium: "Medium (26–50)",
  Large: "Large (51–100)",
  XLarge: "X Large (101–250)",
  Enterprise: "Enterprise (>250 — bespoke)",
};

const formatDateUk = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const daysSince = (iso: string | null): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
};

export function SettingsBillingTier() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const isPlatformAdmin = user?.isPlatformAdmin ?? false;

  const [tier, setTier] = useState<ActiveOrgTier | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [info, setInfo] = useState<string | null>(null);
  const [employeeCount, setEmployeeCount] = useState<string>("");
  const [autoAdjust, setAutoAdjust] = useState(true);
  const [override, setOverride] = useState<Tier | "">("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api<{ organisation: ActiveOrgTier & { uuid: string } }>(
        "/organisations/active",
      );
      const o = res.organisation;
      setTier({
        companySizeTier: o.companySizeTier,
        employeeCount: o.employeeCount,
        autoAdjustBillingTier: o.autoAdjustBillingTier,
        billingTierOverride: o.billingTierOverride,
        lastTierReviewAt: o.lastTierReviewAt,
        effectiveBillingTier: o.effectiveBillingTier,
        isBillingExempt: o.isBillingExempt,
      });
      setEmployeeCount(o.employeeCount === null ? "" : String(o.employeeCount));
      setAutoAdjust(o.autoAdjustBillingTier);
      setOverride(o.billingTierOverride ?? "");
    } catch (err) {
      setErrors(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const submitEmployeeCount = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setErrors([]);
    setInfo(null);
    try {
      const n = Number(employeeCount);
      const res = await api<{ ok: boolean; stripeRepriceError?: string }>(
        "/organisations/active/employee-count",
        { method: "POST", body: { employeeCount: n } },
      );
      setInfo(
        res.stripeRepriceError
          ? `Saved, but Stripe re-pricing could not run automatically: ${res.stripeRepriceError}. Re-try or contact support.`
          : "Employee count and tier updated.",
      );
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitOverride = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setErrors([]);
    setInfo(null);
    try {
      await api("/organisations/active/billing-tier", {
        method: "POST",
        body: {
          autoAdjustBillingTier: autoAdjust,
          billingTierOverride: autoAdjust ? null : override === "" ? null : override,
        },
      });
      setInfo("Billing tier override updated.");
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const reviewAge = daysSince(tier?.lastTierReviewAt ?? null);
  const showQuarterlyNudge = reviewAge !== null && reviewAge >= 90;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          Company size and billing tier
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Your billing tier is set by employee count. The Core Fee + per-module fees both scale with
          tier. Update your headcount when it changes — auto-adjust will re-price your Stripe
          subscription with proration.
        </p>
      </div>

      {showQuarterlyNudge ? (
        <div
          role="region"
          aria-label="Quarterly review nudge"
          className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          It&apos;s been {reviewAge} days since your last tier review. Please confirm or update your
          employee count below.
        </div>
      ) : null}

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}
      {info ? (
        <div
          role="status"
          className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100"
        >
          {info}
        </div>
      ) : null}

      {tier === null ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : (
        <>
          <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Current state
            </h2>
            <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <dt className="text-slate-500 dark:text-slate-400">Employee count</dt>
              <dd className="text-slate-900 dark:text-slate-100">
                {tier.employeeCount ?? "Not set"}
              </dd>
              <dt className="text-slate-500 dark:text-slate-400">Company size tier</dt>
              <dd className="text-slate-900 dark:text-slate-100">
                {tier.companySizeTier ? TIER_LABEL[tier.companySizeTier] : "Not set"}
              </dd>
              <dt className="text-slate-500 dark:text-slate-400">Effective billing tier</dt>
              <dd className="text-slate-900 dark:text-slate-100">
                {tier.isBillingExempt
                  ? "Comped (no Stripe billing)"
                  : tier.effectiveBillingTier
                    ? TIER_LABEL[tier.effectiveBillingTier]
                    : "Not set"}
              </dd>
              <dt className="text-slate-500 dark:text-slate-400">Auto-adjust</dt>
              <dd className="text-slate-900 dark:text-slate-100">
                {tier.autoAdjustBillingTier
                  ? "On"
                  : `Off (override: ${tier.billingTierOverride ?? "—"})`}
              </dd>
              <dt className="text-slate-500 dark:text-slate-400">Last reviewed</dt>
              <dd className="text-slate-900 dark:text-slate-100">
                {formatDateUk(tier.lastTierReviewAt)}
              </dd>
            </dl>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Update employee count
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Self-declared. Use FTE or your authoritative HR figure. Includes contractors who use
              the system regularly.
            </p>
            <form onSubmit={submitEmployeeCount} className="mt-3 flex flex-wrap items-end gap-3">
              <label className="block text-sm">
                <span className="block text-xs font-medium text-slate-700 dark:text-slate-200">
                  Employees
                </span>
                <input
                  type="number"
                  min={0}
                  required
                  value={employeeCount}
                  onChange={(e) => setEmployeeCount(e.target.value)}
                  className="mt-1 w-32 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                />
              </label>
              <button
                type="submit"
                disabled={busy}
                className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </form>
          </section>

          {isPlatformAdmin ? (
            <section className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-900/40 dark:bg-purple-500/10">
              <h2 className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                Platform Admin: billing tier override
              </h2>
              <p className="mt-1 text-xs text-purple-900 dark:text-purple-200">
                Pin a billing tier independent of the employee count. Useful for retention deals
                (&ldquo;keep at Micro pricing despite 11 employees&rdquo;) and grandfathered
                customers.
              </p>
              <form onSubmit={submitOverride} className="mt-3 space-y-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={autoAdjust}
                    onChange={(e) => setAutoAdjust(e.target.checked)}
                    className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span>Auto-adjust billing tier from employee count (default)</span>
                </label>

                {!autoAdjust ? (
                  <label className="block text-sm">
                    <span className="block text-xs font-medium text-purple-900 dark:text-purple-200">
                      Pin billing tier to
                    </span>
                    <select
                      value={override}
                      onChange={(e) => setOverride(e.target.value as Tier | "")}
                      className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                    >
                      <option value="">— Use computed company size tier —</option>
                      <option value="Micro">Micro (≤10)</option>
                      <option value="Small">Small (11–25)</option>
                      <option value="Medium">Medium (26–50)</option>
                      <option value="Large">Large (51–100)</option>
                      <option value="XLarge">X Large (101–250)</option>
                    </select>
                  </label>
                ) : null}

                <button
                  type="submit"
                  disabled={busy}
                  className="rounded-md bg-purple-700 px-3 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60"
                >
                  {busy ? "Saving…" : "Save override"}
                </button>
              </form>
            </section>
          ) : null}

          <p className="text-xs text-slate-500 dark:text-slate-400">
            See{" "}
            <Link
              to={`/o/${slug}/billing`}
              className="text-brand-600 hover:underline dark:text-brand-300"
            >
              Billing
            </Link>{" "}
            for current subscription state and the Stripe Customer Portal.
          </p>
        </>
      )}
    </div>
  );
}
